import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileText, Globe, Loader2, Lock, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { downloadPdf } from "@/components/AuthImg";
import { subjectLabel } from "@/lib/subjects";
import TestScoreHistory from "@/components/TestScoreHistory";

function PaperRow({ p, childId }) {
  const [busy, setBusy] = useState(null);
  const get = (kind) => async () => {
    setBusy(kind);
    try { await downloadPdf(`/test-papers/${p.id}/download/${kind}?child_id=${childId}`, `${p.title}.pdf`); }
    catch { toast.error("Téléchargement impossible · Download failed"); }
    finally { setBusy(null); }
  };
  const btn = "inline-flex items-center gap-1.5 rounded-full border-2 border-slate-900 px-3 py-1.5 text-sm font-bold";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-white border-2 border-slate-200 p-3" data-testid={`parent-tp-${p.id}`}>
      <FileText className="text-blue-600 shrink-0" size={22} />
      <div className="flex-1 min-w-[180px]">
        <p className="font-bold" data-testid="parent-tp-title">{p.title}</p>
        <p className="text-xs text-slate-500">{p.pages} page(s){p.online ? " · aussi en ligne pour l'enfant · also online" : ""}
          {p.last_attempt && <span className="ml-1 font-bold text-green-700" data-testid={`parent-tp-score-${p.id}`}>· score {p.last_attempt.score}/{p.last_attempt.total}</span>}</p>
      </div>
      <button onClick={get("paper")} disabled={!!busy} className={`${btn} bg-yellow-300`} data-testid={`parent-tp-download-${p.id}`}>
        {busy === "paper" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Sujet · Paper
      </button>
      {p.has_answers && (
        <button onClick={get("answers")} disabled={!!busy} className={`${btn} bg-white`} data-testid={`parent-tp-answers-${p.id}`}>
          {busy === "answers" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Corrigé · Answers
        </button>
      )}
      {p.online && <Globe size={16} className="text-green-600" title="Online" />}
    </div>
  );
}

function PackButtons({ c, subject, list }) {
  const [busy, setBusy] = useState(null);
  const keys = list.filter((p) => p.has_answers).length;
  const get = (kind) => async () => {
    setBusy(kind);
    try { await downloadPdf(`/test-papers/pack/${kind}?child_id=${c.id}&subject=${encodeURIComponent(subject)}`, `${subject} pack.pdf`); }
    catch { toast.error("Téléchargement impossible · Download failed"); }
    finally { setBusy(null); }
  };
  const btn = "inline-flex items-center gap-1.5 rounded-full border-2 border-slate-900 px-3 py-1 text-xs font-bold bg-white disabled:opacity-50";
  return (
    <span className="inline-flex flex-wrap gap-2 ml-2">
      <button onClick={get("paper")} disabled={!!busy} className={btn} data-testid={`parent-tp-pack-paper-${c.id}-${subject}`}>
        {busy === "paper" ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />} Tous les sujets · All papers ({list.length})
      </button>
      {keys > 0 && (
        <button onClick={get("answers")} disabled={!!busy} className={btn} data-testid={`parent-tp-pack-answers-${c.id}-${subject}`}>
          {busy === "answers" ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />} Tous les corrigés · All answers ({keys})
        </button>
      )}
    </span>
  );
}

function ChildBlock({ c }) {
  const bySubject = c.papers.reduce((m, p) => ({ ...m, [p.subject]: [...(m[p.subject] || []), p] }), {});
  return (
    <section className="rounded-3xl border-2 border-slate-900 bg-[#F8FAFC] p-5 shadow-[4px_4px_0_0_#0F172A]" data-testid={`parent-tp-child-${c.id}`}>
      <h2 className="text-xl font-bold">{c.name} · Grade {c.grade}</h2>
      {!c.papers.length && <p className="text-sm text-slate-500 mt-2" data-testid={`parent-tp-none-${c.id}`}>Pas encore de sujet pour ses matières · No test paper yet for this child's subjects.</p>}
      {Object.entries(bySubject).map(([s, list]) => (
        <div key={s} className="mt-4">
          <p className="font-bold text-sm text-slate-600 mb-2 flex flex-wrap items-center gap-y-1">{subjectLabel(s, "fr")}{subjectLabel(s, "fr") !== s ? ` · ${s}` : ""}<PackButtons c={c} subject={s} list={list} /></p>
          <div className="space-y-2">{list.map((p) => <PaperRow key={p.id} p={p} childId={c.id} />)}</div>
        </div>
      ))}
      {c.locked_subjects.length > 0 && (
        <p className="mt-4 text-sm text-slate-600 flex flex-wrap items-center gap-1.5" data-testid={`parent-tp-locked-${c.id}`}>
          <Lock size={14} /> Aussi disponibles avec un abonnement · Also available with a plan: {c.locked_subjects.join(", ")} ·
          <Link to="/pricing" className="underline font-bold" data-testid={`parent-tp-plans-${c.id}`}>Voir les forfaits · See plans</Link>
        </p>
      )}
    </section>
  );
}

export default function ParentTestPapers() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/test-papers").then(({ data }) => setData(data)).catch(() => setData({ children: [] })); }, []);
  return (
    <div className="min-h-screen bg-[#FFFDF6] font-parent text-slate-900" data-testid="parent-test-papers">
      <div className="max-w-4xl mx-auto px-5 py-8">
        <Button variant="ghost" onClick={() => navigate("/parent")} className="rounded-full" data-testid="parent-tp-back"><ArrowLeft size={18} className="mr-1" /> Retour · Back</Button>
        <h1 className="text-4xl sm:text-5xl font-kids font-bold mt-4">Sujets d'examen</h1>
        <p className="mt-2 text-slate-600">Test papers to download and print, for your child's grade and subjects. Your name appears discreetly on every page. · À télécharger et imprimer ; votre nom apparaît discrètement sur chaque page.</p>
        {!data ? <Loader2 className="animate-spin mt-10" /> : (
          <div className="mt-8 space-y-6">
            {!data.children.length && <p data-testid="parent-tp-no-kids">Ajoutez d'abord un enfant · Add a child first.</p>}
            {data.children.map((c) => <ChildBlock key={c.id} c={c} />)}
            {data.children.length > 0 && <TestScoreHistory />}
          </div>
        )}
      </div>
    </div>
  );
}
